import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
// import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown"; // Poolcorp Custom Code
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";

/* Poolcorp Custom Code - Start */
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { css } from "styled-components";
/* Poolcorp Custom Code - End */

interface OwnProps {
    extendedStyles?: ChangeCustomerShipToSelectorStyles;
    setParameter: (parameter: GetShipTosApiParameter) => void;
    parameter: GetShipTosApiParameter;
    searchText: string;
    setSearchText: (searchText: string) => void;
    enableWarehousePickup: boolean;
    fulfillmentMethod: string;
    shipTo?: ShipToModel;
    billToId?: string;
    allowSelectBillTo?: boolean;
    onSelect: (shipTo: ShipToModel, isDefault?: boolean) => void; // Poolcorp Custom Code
    onCreateNewAddressClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    isLoading?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    shipTosDataView: getShipTosDataView(state, props.parameter),
});

export interface ChangeCustomerShipToSelectorStyles {
    shipToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

/* Poolcorp Custom Code - Start */
export const changeCustomerShipToSelectorStyles: ChangeCustomerShipToSelectorStyles = {
    shipToSelectorDynamicDropdown: {
        clearClickable: {
            css: css`
                right: 43px !important;
            `,
        },
    },
};
/* Poolcorp Custom Code - End */

const ChangeCustomerShipToSelector: FC<Props> = ({
    extendedStyles,
    shipTo,
    billToId,
    shipTosDataView,
    onSelect,
    parameter,
    setParameter,
    searchText,
    setSearchText,
    enableWarehousePickup,
    fulfillmentMethod,
    isLoading,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerShipToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [hasClear, setHasClear] = useState(false); // Poolcorp Custom Code

    let dropdownLabel = translate("Select Ship To");
    let dropdownPlaceholder = translate("Search or Select Ship To");

    if (enableWarehousePickup && fulfillmentMethod === FulfillmentMethod.PickUp) {
        dropdownLabel = translate("Recipient Address");
        dropdownPlaceholder = translate("Search or Select Recipient Address");
    }

    useEffect(() => {
        setSearchText("");
        setParameter({ ...parameter, billToId, filter: undefined });
    }, [billToId]);

    useEffect(() => {
        setParameter({ ...parameter, filter: searchText || undefined });
    }, [searchText]);

    useEffect(() => {
        if (shipTosDataView.value) {
            const options: OptionObject[] = shipTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);
        }
    }, [shipTosDataView]);

    /* Poolcorp Custom Code */
    const [executedOnce, setExecutedOnce] = useState(false);

    useEffect(() => {
        if (options && !executedOnce && shipTosDataView.value) {
            let defaultShipTo: ShipToModel | undefined;
            let rememberDefault = false;
            
            shipTosDataView.value.forEach(shipTo => {
                if (shipTo.properties['isDefaultShipToLogin'] === "True") {
                    defaultShipTo = shipTo;
                    rememberDefault = true;
                }
            });

            if (defaultShipTo) {
                setSelectedShipTo(defaultShipTo as ShipToModel, rememberDefault);
            }

            setExecutedOnce(true);
        }
    }, [options, executedOnce, shipTosDataView.value]);

    const setSelectedShipTo = (shipTo: ShipToModel, isDefault?: boolean) => {
        onSelect(shipTo, isDefault);
    };
    /* Poolcorp Custom Code */

    const selectCustomerHandler = (shipToId?: string) => {
        if (shipTosDataView.value && shipToId) {
            const shipTo = shipTosDataView.value.filter(shipTo => shipTo.id === shipToId)[0];
            if (shipTo) {
                setSelectedShipTo(shipTo as ShipToModel); // Poolcorp Custom Code
            }
        }
    };

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.currentTarget.value);
        setHasClear(!!(event.currentTarget.value && event.currentTarget.value !== "")); // Poolcorp Custom Code
    };

    /* Poolcorp Custom Code */
    const clearButtonHandler = () => {
        setParameter({
            ...parameter,
            filter: "",
        });
        setHasClear(false);
    };

    const onOpen = (event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | undefined) => {
        setHasClear(false);
        setParameter({
            ...parameter,
            filter: "",
        });
    };

    const onClose = (event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | undefined) => {
        setHasClear(false);
    };
    /* Poolcorp Custom Code */

    return (
        <DynamicDropdown
            {...styles.shipToSelectorDynamicDropdown}
            label={dropdownLabel}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={shipTo?.id}
            placeholder={dropdownPlaceholder}
            isLoading={isLoading}
            options={options}
			search={searchText}
            data-test-selector="changeCustomerShipToSelector"
            filterOption={() => true}
            // Poolcorp Custom Code
            showClear={hasClear}
            clearOnClick={clearButtonHandler}
            onOpen={onOpen}            
            onClose={onClose}
        />
    );
};

export default connect(mapStateToProps)(ChangeCustomerShipToSelector);
